import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import type { Role } from '@hrlabs/shared-types';
import { auth } from './firebase';

/**
 * Optional sign-in allowlist. `VITE_ALLOWED_AUTH_DOMAINS` is a comma-separated
 * list of email domains (e.g. "hrlabs.bg,cloudsource.bg"). When empty, any
 * Google/password account may authenticate — access is still gated by the
 * `role` custom claim (RequireRole + Firestore rules), which only an existing
 * admin (or the ADC bootstrap script) can grant.
 */
const ALLOWED_DOMAINS = (import.meta.env.VITE_ALLOWED_AUTH_DOMAINS ?? '')
  .split(',')
  .map((s: string) => s.trim().toLowerCase())
  .filter(Boolean);

function emailDomainAllowed(email: string | null | undefined): boolean {
  if (ALLOWED_DOMAINS.length === 0) return true;
  const domain = (email ?? '').split('@')[1]?.toLowerCase() ?? '';
  return ALLOWED_DOMAINS.includes(domain);
}

/** Thrown when a sign-in succeeds but the account is outside the allowlist. */
export class AuthDomainError extends Error {}

const DOMAIN_MESSAGE = 'Please sign in with an authorized HRLabs account.';

interface AuthState {
  user: User | null;
  role: Role | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onIdTokenChanged(auth, async (u) => {
      if (u && !emailDomainAllowed(u.email)) {
        await signOut(auth);
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }
      setUser(u);
      if (u) {
        const token = await u.getIdTokenResult();
        setRole((token.claims.role as Role | undefined) ?? null);
      } else {
        setRole(null);
      }
      setLoading(false);
    });
  }, []);

  const value: AuthState = {
    user,
    role,
    loading,
    loginWithGoogle: async () => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const cred = await signInWithPopup(auth, provider);
      if (!emailDomainAllowed(cred.user.email)) {
        await signOut(auth);
        throw new AuthDomainError(DOMAIN_MESSAGE);
      }
    },
    login: async (email, password) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (!emailDomainAllowed(cred.user.email)) {
        await signOut(auth);
        throw new AuthDomainError(DOMAIN_MESSAGE);
      }
    },
    logout: async () => {
      await signOut(auth);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
